import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import classNames from 'classnames'
import {memo, useCallback, useMemo} from 'react'
import * as React from 'react'

import Icon from '../../../../../../common/Icon/Icon'
import ValueToClickAction from '../../../../../../common/ValueToClickAction/ValueToClickAction'
import {OrderByField, OrderDirection} from '../../../../../../packages/Tests/Tests.types'

import type {Props} from './TableHeader.types'

import styles from './TableHeader.css'

function TableHeader({
  orderBy,
  order,
  hasPermissionForMute,
  hasPermissionForInvestigation,
  testsCount,
  selectedTestsCount,
  onToggleSelectAllTests,
  onChangeQueryParams,
}: Props) {
  const isSelectedAll = testsCount === selectedTestsCount
  const onChangeSorting = useCallback(
    (newOrderBy: string) => {
      let newOrder = newOrderBy === OrderByField.DURATION ? OrderDirection.DESC : OrderDirection.ASC

      if (newOrderBy === orderBy) {
        newOrder = order === OrderDirection.ASC ? OrderDirection.DESC : OrderDirection.ASC
      }

      onChangeQueryParams({
        orderBy: newOrderBy,
        order: newOrder,
      })
    },
    [orderBy, order, onChangeQueryParams],
  )
  const renderColumn = useCallback(
    (type: string, name: string, withSorting = false, checkbox?: React.ReactElement | null) => {
      const icon = order === OrderDirection.ASC ? 'chevron-up' : 'chevron-down'
      const sortable = (
        <ValueToClickAction
          value={type}
          onClick={onChangeSorting}
          child={
            <button
              type="button"
              className={styles.columnTitle}
              data-test-build-tests-list={`${name.toLowerCase()}-col`}
            >
              {orderBy === type && <Icon name={icon} />}
              {name}
            </button>
          }
        />
      )
      const nonSortable = <span>{name}</span>
      return (
        <div
          className={classNames({
            [styles.statusColumn]: type === OrderByField.STATUS,
            [styles.nameColumn]: type === OrderByField.NAME,
            [styles.durationColumn]: type === OrderByField.DURATION,
            [styles.runOrderColumn]: type === OrderByField.RUN_ORDER,
          })}
          key={type}
        >
          {checkbox}
          {withSorting ? sortable : nonSortable}
        </div>
      )
    },
    [orderBy, order, onChangeSorting],
  )

  const rendedCheckbox = useMemo(
    () =>
      hasPermissionForMute === true || hasPermissionForInvestigation === true ? (
        <div>
          <Checkbox
            aria-label="Select all tests"
            title={isSelectedAll ? 'Deselect' : 'Select all tests'}
            checked={isSelectedAll}
            onChange={onToggleSelectAllTests}
          />
        </div>
      ) : null,
    [hasPermissionForMute, hasPermissionForInvestigation, isSelectedAll, onToggleSelectAllTests],
  )

  return (
    <div className={styles.tableHeader} data-test-build-tests-list={'table-header'}>
      {[
        renderColumn(
          OrderByField.STATUS,
          selectedTestsCount > 0 ? `${selectedTestsCount} selected` : 'Status',
          false,
          rendedCheckbox,
        ),
        renderColumn(OrderByField.NAME, 'Name', true),
        renderColumn(OrderByField.DURATION, 'Duration', true),
        renderColumn(OrderByField.RUN_ORDER, 'Order', true),
        <div key="actions" className={styles.actionsColumn} />,
      ].filter(Boolean)}
    </div>
  )
}

export default memo<Props>(TableHeader)
